import type { Column } from 'exceljs';
import { quantityMetricOf, REPORT_METRIC_IDS, type ReportMetricId } from '@/composables/inventory/inventoryReport';
import type { MedicineReportFarm } from '@/types';

/**
 * Writes the stock-count report to a workbook, one worksheet per farm: the drugs across, the metrics down.
 *
 * Returns the blob rather than downloading it, and takes every label as an argument: naming and saving
 * the file are the view's decisions, and keeping the DOM and the i18n plugin out of here leaves the
 * workbook itself checkable on its own.
 *
 * Cells are real numbers with a number format, not pre-formatted strings, so the recipient can sort,
 * pivot and chart them. The deviation is written as a fraction under a percent format for the same reason.
 */

const LABEL_COLUMN_WIDTH = 24;
const VALUE_COLUMN_WIDTH = 18;

/** Excel forbids several characters in sheet names and caps them at 31 characters; ExcelJS throws on a name that breaks either rule, which would fail the whole export. */
const MAX_SHEET_NAME_LENGTH = 31;

function sheetNameOf(label: string, taken: Set<string>): string {
    const base = label.replace(/[\\/*?:[\]]/g, '-').slice(0, MAX_SHEET_NAME_LENGTH) || 'Sheet';
    let name = base;

    // Two farms can share a name, and truncation can make different names collide; sheet names are unique, case-insensitively.
    for (let suffix = 2; taken.has(name.toLowerCase()); suffix += 1) {
        const tail = ` (${suffix})`;

        name = `${base.slice(0, MAX_SHEET_NAME_LENGTH - tail.length)}${tail}`;
    }
    taken.add(name.toLowerCase());

    return name;
}

export interface InventoryReportWorkbookOptions {
    farms: MedicineReportFarm[];
    /** Localised row labels by metric. */
    metricLabels: Record<ReportMetricId, string>;
    /** Localised header of the metric column. */
    metricHeader: string;
    /** The period line of a sheet, e.g. "5 Sep 2026 - 2 Oct 2026". Supplied so this stays free of i18n and date formatting. */
    periodLabel: (farm: MedicineReportFarm) => string;
    /** Localised label of a drug-and-unit column. */
    columnLabel: (column: MedicineReportFarm['columns'][number]) => string;
}

export async function buildInventoryReportWorkbook(options: InventoryReportWorkbookOptions): Promise<Blob> {
    const ExcelJS = await import('exceljs');

    const workbook = new ExcelJS.Workbook();
    workbook.created = new Date();

    const takenNames = new Set<string>();

    options.farms.forEach((farm) => {
        const sheet = workbook.addWorksheet(sheetNameOf(farm.farm_name, takenNames));

        sheet.columns = [{ width: LABEL_COLUMN_WIDTH }, ...farm.columns.map(() => ({ width: VALUE_COLUMN_WIDTH }))] as Column[];

        const title = sheet.addRow([farm.farm_name]);
        title.font = { bold: true };
        sheet.addRow([options.periodLabel(farm)]);
        sheet.addRow([]);

        const header = sheet.addRow([options.metricHeader, ...farm.columns.map(options.columnLabel)]);
        header.font = { bold: true };

        REPORT_METRIC_IDS.forEach((metric) => {
            const row = sheet.addRow([
                options.metricLabels[metric],
                ...farm.columns.map((column) => {
                    if (metric === 'deviation') {
                        return column.deviation_percent === null ? null : column.deviation_percent / 100;
                    }

                    return quantityMetricOf(column, metric);
                }),
            ]);

            row.getCell(1).font = { bold: true };

            // Three decimals is what a quantity carries, and trailing zeros are not shown.
            row.eachCell((cell, columnNumber) => {
                if (columnNumber > 1) {
                    cell.numFmt = metric === 'deviation' ? '0.0%' : '#,##0.###';
                    cell.alignment = { horizontal: 'right' };
                }
            });
        });
    });

    // ExcelJS declares its own Buffer type rather than reusing ArrayBuffer, so the cast is needed to hand it to Blob.
    const buffer = (await workbook.xlsx.writeBuffer()) as ArrayBuffer;

    return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}
